import React, { FC, useCallback, useContext, useEffect } from 'react';
import { CurrentUserContext } from '../contexts/CurrentUserContext';
import { styled } from '@mui/material';
import TableCell from '@mui/material/TableCell';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import { useTranslation } from 'react-i18next';
import { DateFormat } from '../helpers/dateFormat';
import { Date } from '../components/Date/Date';
import { apiUser, TUpdateUserPayload } from '../resources/api/apiUser';
import { PageHeader } from '../components/PageHeader/PageHeader';
import { useForm } from 'react-hook-form';
import { useDialog } from '../hooks/useDialog';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { EditUserForm } from '../components/EditUserDialog/EditUserForm';
import DialogActions from '@mui/material/DialogActions';
import { useApiErrorHandler } from '../hooks/useApiErrorHandler';
import { TypographyH4 } from '../components/Typography/Typography';
import { ChangePassword } from '../components/ChangePassword/ChangePassword';
import Box from '@mui/system/Box';
import Grid from '@mui/material/Grid';
import { MfaSettings } from '../components/MfaSettings/MfaSettings';
import Alert from '@mui/material/Alert';
import { useResourceChecker } from '../resources/resource';
import { NotificationContext } from '../contexts/NotificationContext';
import { IUserInfo } from '../resources/models/IUserInfo';
import { MFADevicesFetcher } from '../fetchers/MfaDevicesFetcher';
import { MFAMethodsFetcher } from '../fetchers/MfaMethodsFetcher';

const TitleCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.text.disabled,
  width: '23vw',
}));

const { useUpdateUser, useRequestEmailChange } = apiUser;

export const ViewAccountPage: FC = () => {
  const { t } = useTranslation();
  const { userInfo, refetchUserInfo } = useContext(CurrentUserContext);
  const { addNotification } = useContext(NotificationContext);
  const handleError = useApiErrorHandler();
  const resourceCheck = useResourceChecker();

  const { execute: executeEdit, Guard: GuardEdit } = useUpdateUser(`${userInfo?.id}`);
  const { execute: executeEmailChange, resource: resourceEmailChange } = useRequestEmailChange(userInfo?.id);

  const canChangeEmail = resourceCheck(resourceEmailChange);

  const form = useForm();

  const { isOpen: isEdit, openUrl: editUrl, close: editClose } = useDialog('edit');

  useEffect(() => {
    if (isEdit) {
      form.reset({
        first_name: userInfo?.first_name,
        last_name: userInfo?.last_name,
        email: userInfo?.email,
        phone_number: userInfo?.phone_number,
      });
    }
  }, [isEdit]);

  const email = form.watch('email');

  const emailUpdate = canChangeEmail && userInfo && (email ?? '').trim() !== userInfo.email.trim();

  const onEdit = useCallback(
    async ({ email, ...fields }: TUpdateUserPayload & { email: string }) => {
      try {
        if (Object.entries(fields).some(([key, value]) => value !== userInfo?.[key as keyof IUserInfo])) {
          if (fields.phone_number === '') {
            fields.phone_number = null;
          }
          await executeEdit(fields);
          addNotification(
            {
              severity: 'success',
              message: t('viewAccount.edit.success'),
            },
            false
          );
        }

        if (canChangeEmail && userInfo && email.trim() !== userInfo.email.trim()) {
          await executeEmailChange(email.trim());
          addNotification(
            {
              severity: 'success',
              message: t('viewAccount.edit.emailChangeSuccess'),
            },
            false
          );
        }
        await refetchUserInfo();
        editClose();
      } catch (e) {
        handleError(e);
      }
    },
    [executeEdit, executeEmailChange, userInfo, canChangeEmail]
  );

  if (!userInfo) return null;

  return (
    <>
      <PageHeader title={t('viewAccount.header')}>
        <GuardEdit>
          <Button variant={'contained'} component={Link} to={editUrl}>
            {t('viewAccount.edit.button')}
          </Button>

          <Dialog open={isEdit}>
            <DialogTitle>{t('viewAccount.edit.title')}</DialogTitle>
            <DialogContent>
              <EditUserForm
                user={userInfo}
                onSubmit={onEdit}
                control={form.control}
                handleSubmit={form.handleSubmit}
                formState={form.formState}
                showEmail={canChangeEmail}
              />
              {canChangeEmail && emailUpdate ? (
                <Box mt={2}>
                  <Alert severity={'info'}>{t('viewAccount.edit.emailChange')}</Alert>
                </Box>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button variant={'text'} onClick={editClose}>
                {t('common.dialog.close')}
              </Button>
              <Button variant={'contained'} onClick={form.handleSubmit(onEdit)}>
                {t('common.dialog.submit')}
              </Button>
            </DialogActions>
          </Dialog>
        </GuardEdit>
      </PageHeader>

      <Table>
        <TableBody>
          <TableRow>
            <TitleCell>{t('common.tableHeader.name')}</TitleCell>
            <TableCell>
              {userInfo.first_name} {userInfo.last_name}
            </TableCell>
          </TableRow>
          <TableRow>
            <TitleCell>{t('common.tableHeader.email')}</TitleCell>
            <TableCell>{userInfo.email}</TableCell>
          </TableRow>
          <TableRow>
            <TitleCell>{t('common.tableHeader.phone')}</TitleCell>
            <TableCell>{userInfo.phone_number}</TableCell>
          </TableRow>
          <TableRow>
            <TitleCell>{t('viewAccount.lastUpdateLabel')}</TitleCell>
            <TableCell>
              <Date date={userInfo.updated_at} format={DateFormat.DateWithFullTime} />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Box mt={5}>
        <TypographyH4>{t('security.title')}</TypographyH4>
        <Box mt={5} pl={2}>
          <Grid container>
            <Grid item xs={12} md={8} lg={6} xl={4}>
              <ChangePassword />
            </Grid>
          </Grid>
        </Box>

        <Box mt={5} pl={2}>
          <Grid container>
            <Grid item xs={12} md={8} lg={6} xl={4}>
              <MFADevicesFetcher.WAF>
                <MFAMethodsFetcher.WAF>
                  <MfaSettings />
                </MFAMethodsFetcher.WAF>
              </MFADevicesFetcher.WAF>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};
